/** Immutable helpers over the scene's element tree. */
import type { Element, GroupElement, ClipMaskElement, Scene } from '@prism-graphics/scene-schema';

export type Container = GroupElement | ClipMaskElement;

export const isContainer = (el: Element | undefined): el is Container => el !== undefined && (el.type === 'group' || el.type === 'clipMask');

export function walk(elements: readonly Element[], visit: (el: Element, parent: Container | undefined, index: number) => void, parent?: Container): void {
  elements.forEach((el, i) => {
    visit(el, parent, i);
    if (isContainer(el)) walk(el.children, visit, el);
  });
}

export function findElement(scene: Scene, id: string): Element | undefined {
  let found: Element | undefined;
  walk(scene.elements, (el) => {
    if (el.id === id) found = el;
  });
  return found;
}

/** The parent container of `id` (undefined for top-level elements and unknown ids). */
export function parentOf(scene: Scene, id: string): Container | undefined {
  let found: Container | undefined;
  walk(scene.elements, (el, parent) => {
    if (el.id === id) found = parent;
  });
  return found;
}

/** Ids from the top level down to `id` (inclusive); empty when unknown. */
export function pathTo(scene: Scene, id: string): string[] {
  const search = (els: readonly Element[], trail: string[]): string[] | undefined => {
    for (const el of els) {
      const t = [...trail, el.id];
      if (el.id === id) return t;
      if (isContainer(el)) {
        const r = search(el.children, t);
        if (r) return r;
      }
    }
    return undefined;
  };
  return search(scene.elements, []) ?? [];
}

export function siblingsOf(scene: Scene, id: string): readonly Element[] {
  const p = parentOf(scene, id);
  return p ? p.children : scene.elements;
}

export function indexIn(scene: Scene, id: string): number {
  return siblingsOf(scene, id).findIndex((e) => e.id === id);
}

export function allIds(scene: Scene): Set<string> {
  const ids = new Set<string>();
  walk(scene.elements, (el) => ids.add(el.id));
  return ids;
}

/** Whether `id` is `ancestor` or inside it. */
export function isWithin(scene: Scene, id: string, ancestor: string): boolean {
  return pathTo(scene, id).includes(ancestor);
}

/** Drop ids whose ancestor is also in the list (moving a group moves its children). */
export function topmost(scene: Scene, ids: readonly string[]): string[] {
  const set = new Set(ids);
  return ids.filter((id) => !pathTo(scene, id).slice(0, -1).some((a) => set.has(a)));
}

/** Map every element (depth first); `fn` returns the replacement (or the same object). */
export function mapElements(elements: readonly Element[], fn: (el: Element) => Element): Element[] {
  let changed = false;
  const out = elements.map((el) => {
    let next = el;
    if (isContainer(el)) {
      const children = mapElements(el.children, fn);
      if (children !== el.children) next = { ...el, children } as Element;
    }
    const mapped = fn(next);
    if (mapped !== el) changed = true;
    return mapped;
  });
  return changed ? out : (elements as Element[]);
}

export function updateElement(scene: Scene, id: string, fn: (el: Element) => Element): Scene {
  const elements = mapElements(scene.elements, (el) => (el.id === id ? fn(el) : el));
  return elements === scene.elements ? scene : { ...scene, elements };
}

/** Remove elements by id (anywhere in the tree). */
export function removeFromTree(elements: readonly Element[], ids: ReadonlySet<string>): Element[] {
  return elements
    .filter((el) => !ids.has(el.id))
    .map((el) => (isContainer(el) ? ({ ...el, children: removeFromTree(el.children, ids) } as Element) : el));
}

/** Insert elements into a parent (null = top level) at an index (default: end). */
export function insertInto(elements: readonly Element[], parentId: string | null, index: number | undefined, items: readonly Element[]): Element[] {
  if (parentId === null) {
    const at = index === undefined ? elements.length : Math.max(0, Math.min(index, elements.length));
    return [...elements.slice(0, at), ...items, ...elements.slice(at)];
  }
  return elements.map((el) => {
    if (!isContainer(el)) return el;
    if (el.id === parentId) return { ...el, children: insertInto(el.children, null, index, items) } as Element;
    return { ...el, children: insertInto(el.children, parentId, index, items) } as Element;
  });
}

/** Display list for the layers tree: front-most first (reverse paint order), with depth. */
export function flattenForLayers(scene: Scene, collapsed: ReadonlySet<string>): Array<{ el: Element; depth: number; parentId: string | null }> {
  const out: Array<{ el: Element; depth: number; parentId: string | null }> = [];
  const visit = (els: readonly Element[], depth: number, parentId: string | null) => {
    for (let i = els.length - 1; i >= 0; i--) {
      const el = els[i]!;
      out.push({ el, depth, parentId });
      if (isContainer(el) && !collapsed.has(el.id)) visit(el.children, depth + 1, el.id);
    }
  };
  visit(scene.elements, 0, null);
  return out;
}
